import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import type { ActivityItem } from '../components/activity/ActivityCard';

interface UseActivityOptions {
  projectId?: string | null;
  pageSize?: number;
}

interface ActivityResponse {
  items: ActivityItem[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

interface UseActivityReturn {
  activities: ActivityItem[];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  error: string | null;
  loadMore: () => void;
  refresh: () => void;
}

export function useActivity(options: UseActivityOptions = {}): UseActivityReturn {
  const { projectId, pageSize = 20 } = options;
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const fetchActivity = useCallback(
    async (pageNum: number, append: boolean = false) => {
      try {
        if (pageNum === 1) {
          setIsLoading(true);
        } else {
          setIsLoadingMore(true);
        }

        const params = new URLSearchParams({
          page: pageNum.toString(),
          pageSize: pageSize.toString(),
        });

        if (projectId) {
          params.append('projectId', projectId);
        }

        const response = await api.get<ActivityResponse>(`/activity?${params}`);
        const data = response.data;

        if (append) {
          setActivities((prev) => [...prev, ...data.items]);
        } else {
          setActivities(data.items);
        }

        setHasMore(pageNum < data.pagination.totalPages);
        setError(null);
      } catch (err: any) {
        setError(err.message || 'Failed to load activity');
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    },
    [projectId, pageSize]
  );

  // Initial load and when projectId changes
  useEffect(() => {
    setPage(1);
    setActivities([]);
    fetchActivity(1, false);
  }, [projectId, fetchActivity]);

  const loadMore = useCallback(() => {
    if (!isLoadingMore && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchActivity(nextPage, true);
    }
  }, [page, isLoadingMore, hasMore, fetchActivity]);

  const refresh = useCallback(() => {
    setPage(1);
    setActivities([]);
    fetchActivity(1, false);
  }, [fetchActivity]);

  return {
    activities,
    isLoading,
    isLoadingMore,
    hasMore,
    error,
    loadMore,
    refresh,
  };
}
